import { AuthenticationManager, DriverAuthenticationManager, Token } from '@frontend/authentication-v2';
import { fileDownload, getCookie, service_proxy_ports } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { format } from 'date-fns';
import { merge } from 'lodash';

import { notifyApiRequestFailure, notifyApiRequestSuccess } from './api-request-observer';
import { ApiError, ApiQueryParams } from './models';

let lastRequests: { url: string; init?: RequestInit; timestamp: number }[] = [];
const requestTimeout = 2 * 1000;
const maxRequests = 3;

function getServiceName(response: Response): string {
    return new URL(response.url).hostname.split('.')[0];
}

async function throwIfNotOk(response: Response, parser: (r: Response) => Promise<unknown>, label?: string): Promise<void> {
    if (response.ok) return;
    const serviceName = getServiceName(response);
    const url = label ?? response.url;
    let body;
    try {
        body = await parser(response);
    } catch (e) {
        notifyApiResponseFailure(url, response);
        throw new ApiError(`Error fetching ${url}`, response, response.status, serviceName);
    }
    notifyApiResponseFailure(url, response);
    throw new ApiError(`Error fetching ${url}`, body, response.status, serviceName);
}

function notifyApiResponseFailure(url: string, response: Response): void {
    notifyApiRequestFailure(url, new ApiError(`Error fetching ${url}`, undefined, response.status, getServiceName(response)));
}

export abstract class APIClient {
    protected static async apiPaginated<T, S extends string | number>(
        url: string,
        queryParams?: ApiQueryParams<S> | null,
        withCredentials?: Token,
        includeAccessToken = true
    ): Promise<T> {
        const endpoint = buildApiEndpoint(url, { ...queryParams, language: getCookie('language') });
        const response = await this.fetch(endpoint, undefined, includeAccessToken, withCredentials);
        await throwIfNotOk(response, (r) => r.json(), endpoint);
        return await response.json();
    }

    protected static async handleResponse<T>(response: Response): Promise<T> {
        await throwIfNotOk(response, (r) => r.json());
        return (await response.json()) as T;
    }

    protected static async handleVoidResponse(response: Response): Promise<void> {
        await throwIfNotOk(response, (r) => r.json());
    }

    protected static async handleBlobResponse(response: Response): Promise<Blob> {
        await throwIfNotOk(response, (r) => r.blob());
        return await response.blob();
    }

    protected static extractFilename(response: Response): string | null {
        const disposition = response.headers.get('Content-Disposition');
        if (!disposition) return null;
        const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)"?/i);
        return match ? decodeURIComponent(match[1]) : null;
    }

    protected static async handleTextResponse(response: Response): Promise<string> {
        await throwIfNotOk(response, (r) => r.text());
        return await response.text();
    }

    public static async handleArrayBufferResponse(response: Response): Promise<ArrayBuffer> {
        await throwIfNotOk(response, (r) => r.arrayBuffer());
        return await response.arrayBuffer();
    }

    protected static async handleFileDownload(response: Response, name: string): Promise<void> {
        const file = await this.handleBlobResponse(response);
        const time = format(new Date(), 'yyyy-MM-dd HH-mm-ss');
        const fileName = name + ' ' + time;
        fileDownload(file, fileName);
    }

    protected static async fetchWithoutToken<T, S extends number | string>(url: string, queryParams?: ApiQueryParams<S> | null): Promise<T> {
        let endpoint = buildApiEndpoint(url, { ...queryParams });
        if (!endpoint.startsWith('http')) {
            endpoint = api(endpoint);
        }

        const response = await window.fetch(endpoint);
        await throwIfNotOk(response, (r) => r.json(), endpoint);
        return await response.json();
    }

    private static trackRequest(endpoint: string, init?: RequestInit): void {
        lastRequests = lastRequests.filter((request) => Date.now() - request.timestamp < requestTimeout);

        const currentRequest = { url: endpoint, init, timestamp: Date.now() };
        const matchingRequest = lastRequests.find(
            (request) => request.url === currentRequest.url && JSON.stringify(request.init) === JSON.stringify(currentRequest.init)
        );

        if (matchingRequest) {
            Logger.error(`Duplicate request: ${matchingRequest.url}`);
        }

        lastRequests.push(currentRequest);
        if (lastRequests.length > maxRequests) {
            lastRequests.splice(0, lastRequests.length - maxRequests);
        }
    }

    private static async injectAuth(init: RequestInit | undefined, includeAccessToken: boolean, withCredentials?: Token): Promise<RequestInit | undefined> {
        if (!includeAccessToken) return init;
        if (withCredentials) Logger.log('Request sent using override credentials.');
        const credentials = withCredentials || (await AuthenticationManager.getInstance().waitForToken());
        return merge(init, {
            headers: {
                authorization: 'Bearer ' + credentials.jwt_token
            }
        });
    }

    private static async retryOn401(endpoint: string, init: RequestInit | undefined, response: Response): Promise<Response> {
        if (response.status !== 401) return response;
        Logger.error('Unauthorized refreshing token.');
        try {
            await AuthenticationManager.getInstance().refresh();
        } catch (e) {
            const token = localStorage.getItem('token');
            let isIotDriver = false;
            try {
                isIotDriver =
                    token != null &&
                    (JSON.parse(token) as Token).entity_type === 'iot' &&
                    DriverAuthenticationManager.getInstance().provisionInfo !== undefined;
            } catch {
                Logger.error('Invalid token format in localStorage');
            }
            if (isIotDriver) {
                await DriverAuthenticationManager.getInstance().refresh();
            } else {
                await AuthenticationManager.getInstance().doLogout();
            }
        }
        const credentials = await AuthenticationManager.getInstance().waitForToken();
        init = merge(init, {
            headers: {
                authorization: 'Bearer ' + credentials.jwt_token
            }
        });
        return await window.fetch(endpoint, init);
    }

    protected static async fetch(endpoint: string, init?: RequestInit, includeAccessToken = true, withCredentials?: Token): Promise<Response> {
        init = await this.injectAuth(init, includeAccessToken, withCredentials);
        if (!endpoint.startsWith('http')) {
            endpoint = api(endpoint);
        }
        this.trackRequest(endpoint, init);
        try {
            const response = await window.fetch(endpoint, init);
            const finalResponse = await this.retryOn401(endpoint, init, response);
            notifyApiRequestSuccess(endpoint);
            return finalResponse;
        } catch (error) {
            notifyApiRequestFailure(endpoint, error);
            throw error;
        }
    }

    protected static async fetchWithQueryParams<S extends string | number>(
        endpoint: string,
        init?: RequestInit,
        includeAccessToken = true,
        withCredentials?: Token,
        queryParams?: ApiQueryParams<S>
    ): Promise<Response> {
        endpoint = buildApiEndpoint(endpoint, { ...queryParams, language: getCookie('language') });
        return this.fetch(endpoint, init, includeAccessToken, withCredentials);
    }

    protected static async postWithQueryParams<S extends string | number>(
        url: string,
        queryParams?: ApiQueryParams<S> | null,
        data?: any,
        includeAccessToken = true,
        withCredentials?: Token
    ): Promise<Response> {
        const endpoint = buildApiEndpoint(url, queryParams);
        return this.post(endpoint, data, includeAccessToken, withCredentials);
    }

    protected static async post(endpoint: string, data?: any, includeAccessToken = true, withCredentials?: Token): Promise<Response> {
        const isFormData = data instanceof FormData;
        return this.fetch(
            endpoint,
            {
                method: 'POST',
                ...(!isFormData && { headers: { 'Content-Type': 'application/json' } }),
                body: isFormData ? data : JSON.stringify(data)
            },
            includeAccessToken,
            withCredentials
        );
    }

    protected static async put(endpoint: string, data?: any, includeAccessToken = true, withCredentials?: Token): Promise<Response> {
        const isFormData = data instanceof FormData;
        return this.fetch(
            endpoint,
            {
                method: 'PUT',
                ...(!isFormData && { headers: { 'Content-Type': 'application/json' } }),
                body: isFormData ? data : JSON.stringify(data)
            },
            includeAccessToken,
            withCredentials
        );
    }

    protected static async patch(endpoint: string, data?: any, includeAccessToken = true, withCredentials?: Token): Promise<Response> {
        const isFormData = data instanceof FormData;
        return this.fetch(
            endpoint,
            {
                method: 'PATCH',
                ...(!isFormData && { headers: { 'Content-Type': 'application/json' } }),
                body: isFormData ? data : JSON.stringify(data)
            },
            includeAccessToken,
            withCredentials
        );
    }

    protected static async delete(endpoint: string, includeAccessToken = true, withCredentials?: Token): Promise<Response> {
        return this.fetch(
            endpoint,
            {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            },
            includeAccessToken,
            withCredentials
        );
    }

    protected static async resolve<T>(endpoint: string, ids: string[], field: string = 'id'): Promise<T> {
        const response = await this.post(`${endpoint}-resolve?size=${ids.length}`, { ids, field });
        return await this.handleResponse<T>(response);
    }
}

export function buildApiEndpoint<T extends string>(endpoint: string, queryParams?: ApiQueryParams<T> | null): string {
    if (!queryParams) {
        return endpoint;
    }

    // This bizarre construct is necessary because Tyepscript 4.2.3 does not seem to understand that queryParams has
    // string values but instead uses unknown. This looks like a bug in Typescript. If you are using a newer version of
    // Typescript, try removing this parameter and see if it validates.
    const queryParams2: { [key in string]?: string | string[] | undefined | null } = queryParams;
    const searchParams = Object.entries(queryParams2).reduce((result, queryParam) => {
        if (queryParam[1]) {
            if (typeof queryParam[1] === 'string') {
                result.append(queryParam[0], queryParam[1]);
            } else {
                for (const queryParamValue of queryParam[1]) {
                    result.append(queryParam[0], queryParamValue);
                }
            }
        }
        return result;
    }, new URLSearchParams());
    const queryString = decodeURIComponent(searchParams.toString());
    if (queryString !== '') {
        return `${endpoint}?${queryString}`;
    }
    return endpoint;
}

/**
 * TODO update to make sure edge no longer needs a seperate build
 * @param endpoint
 * @returns
 */
export function api(endpoint: string): string {
    const serviceName: string = endpoint.split('/')[1];
    const apiEndpoint = endpoint.replace('/' + serviceName, '');
    if (process.env['NX_PUBLIC_BUILD_ENV'] === 'edge') {
        return 'http://localhost:' + service_proxy_ports[serviceName] + apiEndpoint;
    } else {
        return 'https://' + serviceName + '.' + process.env['NX_PUBLIC_API_DOMAIN'] + apiEndpoint;
    }
}
